import { z } from "zod";
import { CharacteristicValuesInputSchema } from "./batches.js";

// Ordens de Produção (planejamento, separada da execução = ProductionRun).
// Contrato cross-wire (request/response + consumido pelo FE via z.infer). Campos alinhados a
// SimulationBatch (lineId, productId(s) com principal, plannedQty↔targetQuantity) p/ integração
// futura. Datas em ISO 8601 com offset (UTC no wire). A validação de "plannedStartAt no futuro"
//  é feita no SERVICE (400) + no form (UX), não aqui — mantém o schema puro/testável.

export const PRODUCTION_ORDER_STATUSES = [
  "SCHEDULED",
  "RUNNING",
  "COMPLETED",
  "CANCELLED",
] as const;
export const PRODUCTION_ORDER_CANCEL_REASONS = ["LINE_BUSY", "MISSED", "MANUAL"] as const;
// Origem da OP: SCHEDULED (agendada/auto-start) vs MANUAL (start ad-hoc).
export const PRODUCTION_ORDER_ORIGINS = ["SCHEDULED", "MANUAL"] as const;

const ProductionOrderInputSchema = z.object({
  lineId: z.string().uuid(),
  productIds: z.array(z.string().uuid()).min(1, "Selecione ao menos um produto"),
  primaryProductId: z.string().uuid(),
  plannedQty: z.number().int().positive(),
  plannedStartAt: z.string().datetime({ offset: true }),
  expectedEnd: z.string().datetime({ offset: true }),
  // Prazo do cliente. SEM refine contra plannedStartAt de propósito: uma OP pode
  // "nascer atrasada" (prazo anterior à janela planejada) e isso é informação legítima, não erro.
  dueDate: z.string().datetime({ offset: true }).nullable().optional(),
  code: z.string().trim().max(40).optional(),
  responsible: z.string().trim().max(120).optional(),
  observations: z.string().trim().max(1000).optional(),
  // Spec 084 — as características da batelada, informadas no AGENDAMENTO. Na tela, iniciar produção
  // é agendar uma ordem que o worker inicia sozinho, então é aqui que a marcação acontece; o
  // auto-start copia os valores para a batelada. Obrigatoriedade depende da linha e é do service.
  characteristics: CharacteristicValuesInputSchema.optional(),
});

const endAfterStart = (o: { plannedStartAt?: string; expectedEnd?: string }) =>
  !o.plannedStartAt || !o.expectedEnd || new Date(o.expectedEnd) > new Date(o.plannedStartAt);
const primaryInList = (o: { productIds?: string[]; primaryProductId?: string }) =>
  !o.productIds || !o.primaryProductId || o.productIds.includes(o.primaryProductId);

export const CreateProductionOrderRequestSchema = ProductionOrderInputSchema.refine(endAfterStart, {
  path: ["expectedEnd"],
  message: "Fim planejado deve ser após o início",
}).refine(primaryInList, {
  path: ["primaryProductId"],
  message: "O produto principal deve estar entre os produtos selecionados",
});
export type CreateProductionOrderRequest = z.infer<typeof CreateProductionOrderRequestSchema>;

// Update: só em SCHEDULED (guard no service); todos os campos opcionais, mesmas refines tolerantes.
export const UpdateProductionOrderRequestSchema = ProductionOrderInputSchema.partial()
  .refine(endAfterStart, { path: ["expectedEnd"], message: "Fim planejado deve ser após o início" })
  .refine(primaryInList, {
    path: ["primaryProductId"],
    message: "O produto principal deve estar entre os produtos selecionados",
  });
export type UpdateProductionOrderRequest = z.infer<typeof UpdateProductionOrderRequestSchema>;

export const CancelProductionOrderRequestSchema = z
  .object({ detail: z.string().trim().max(500).optional() })
  .strict();
export type CancelProductionOrderRequest = z.infer<typeof CancelProductionOrderRequestSchema>;

// Filtros + paginação (espelha audit.ts).
export const ProductionOrderQueryRequestSchema = z
  .object({
    lineId: z.string().uuid().optional(),
    status: z.enum(PRODUCTION_ORDER_STATUSES).optional(),
    responsible: z.string().optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    // Timeline: com `overlap` + from/to, filtra OPs que SOBREPÕEM a janela visível
    // (plannedStartAt ≤ to AND expectedEnd ≥ from), não só as que começam no período — para uma OP
    // multi-dia aparecer em todos os dias que ela cobre. Sem `overlap`, mantém o filtro por início.
    // Coerção ESTRITA: `z.coerce.boolean()` trataria "false"/"0" como true (qualquer string não-vazia
    // é truthy), invertendo a semântica; só boolean true ou a string "true" habilitam o overlap.
    overlap: z
      .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
      .optional(),
    // "somente atrasadas": mesma coerção ESTRITA do `overlap` acima (um
    // `z.coerce.boolean()` leria "false"/"0" como true e inverteria o filtro).
    lateOnly: z
      .union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")])
      .optional(),
    offset: z.coerce.number().int().nonnegative().default(0),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    path: ["to"],
    message: "Período inválido: 'from' deve ser anterior ou igual a 'to'",
  });
export type ProductionOrderQueryRequest = z.infer<typeof ProductionOrderQueryRequestSchema>;

export const ProductionOrderProductSchema = z.object({
  productId: z.string(),
  name: z.string(),
  isPrimary: z.boolean(),
});

export const ProductionOrderSchema = z.object({
  id: z.string(),
  code: z.string().nullable(),
  lineId: z.string(),
  lineName: z.string(),
  status: z.enum(PRODUCTION_ORDER_STATUSES),
  origin: z.enum(PRODUCTION_ORDER_ORIGINS),
  products: z.array(ProductionOrderProductSchema),
  // Spec 081 (FR-027) — quantidade aceita fração; `goodQtyOfRun` já devolve decimal.
  plannedQty: z.number().finite(),
  plannedStartAt: z.string(),
  expectedEnd: z.string(),
  // Prazo do cliente e atraso DERIVADO (nunca persistido; computado no serializer):
  // projetado (expectedEnd − dueDate) em SCHEDULED/RUNNING, real (completedAt − dueDate) em
  // COMPLETED, null sem prazo ou em CANCELLED. Minutos inteiros com Math.ceil.
  dueDate: z.string().nullable(),
  latenessMin: z.number().int().nullable(),
  startedAt: z.string().nullable(),
  productionRunId: z.string().nullable(),
  cancelReason: z.enum(PRODUCTION_ORDER_CANCEL_REASONS).nullable(),
  cancelDetail: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  responsible: z.string().nullable(),
  observations: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // OEE/produzido preenchidos só p/ COMPLETED (coluna OEE da lista); RUNNING expõe produzido parcial.
  oee: z.number().finite().nullable(),
  // Spec 081 (FR-027) — produzido aceita fração.
  producedQty: z.number().finite().nullable(),
  // Spec 084 — valores informados no agendamento, com o rótulo já resolvido.
  characteristics: z.array(
    z.object({
      characteristicId: z.string(),
      name: z.string(),
      valueId: z.string().nullable(),
      label: z.string(),
    })
  ),
  // Spec 084 — obrigatórias da linha sem valor, por nome. DERIVADO na serialização, nunca gravado:
  // uma ordem agendada antes de a característica existir fica "pendente de marcação".
  missingRequiredCharacteristics: z.array(z.string()),
});
export type ProductionOrder = z.infer<typeof ProductionOrderSchema>;

export const ProductionOrderListResponseSchema = z.object({
  items: z.array(ProductionOrderSchema),
  total: z.number().int(),
  offset: z.number().int(),
  limit: z.number().int(),
});
export type ProductionOrderListResponse = z.infer<typeof ProductionOrderListResponseSchema>;

// Indicadores por OP — parcial por estado: execution é null em SCHEDULED, parcial em RUNNING.
export const ProductionOrderIndicatorsSchema = z.object({
  status: z.enum(PRODUCTION_ORDER_STATUSES),
  planned: z.object({
    plannedQty: z.number().int(),
    plannedStartAt: z.string(),
    expectedEnd: z.string(),
    plannedMin: z.number().int(),
  }),
  // Prazo do cliente: `kind` distingue a projeção (fim planejado) do fato (fim real).
  // Sem prazo (ou OP cancelada) ⇒ tudo null — "sem prazo" nunca vira "no prazo".
  due: z.object({
    dueDate: z.string().nullable(),
    latenessMin: z.number().int().nullable(),
    kind: z.enum(["PROJECTED", "ACTUAL"]).nullable(),
  }),
  execution: z
    .object({
      oee: z.number().finite().nullable(),
      availability: z.number().finite().nullable(),
      performance: z.number().finite().nullable(),
      quality: z.number().finite().nullable(),
      planAdherence: z.number().finite().nullable(),
      producedQty: z.number().finite().nullable(),
      downtimeMin: z.number().int().nullable(),
      scrapPct: z.number().finite().nullable(),
      startDelayMin: z.number().int().nullable(),
      actualDurationMin: z.number().int().nullable(),
      startedAt: z.string().nullable(),
      completedAt: z.string().nullable(),
    })
    .nullable(),
});
export type ProductionOrderIndicators = z.infer<typeof ProductionOrderIndicatorsSchema>;

// ── Ordens simuláveis pelo objetivo "entregar no prazo" ────────────────
// A lista já chega filtrada e anotada pelo servidor: as regras de elegibilidade (exige prazo;
// em andamento exige saldo) são de domínio, não de tela. Itens inelegíveis vêm com o motivo, para
// a interface EXPLICAR em vez de simplesmente omitir — sumir sem dizer por quê é o que faz o
// usuário achar que a ordem não existe.

export const SimulatableOrderReasonSchema = z.enum([
  "NO_DUE_DATE", // sem prazo: não há com o que comparar
  "NO_REMAINING", // em andamento e a quantidade planejada já foi atingida
  // Sem produto principal a simulação não sabe o que produzir; `resolveOpTarget` já
  // recusava com 409, então listar a ordem como elegível só empurrava o erro para depois do clique.
  "NO_PRODUCT",
]);
export type SimulatableOrderReason = z.infer<typeof SimulatableOrderReasonSchema>;

export const SimulatableOrderSchema = z.object({
  id: z.string().uuid(),
  code: z.string().nullable(),
  status: z.enum(["SCHEDULED", "RUNNING"]),
  productId: z.string().uuid().nullable(),
  productName: z.string().nullable(),
  // Spec 081 (FR-027) — quantidade aceita fração; `goodQtyOfRun` já devolve decimal.
  plannedQty: z.number().finite(),
  plannedStartAt: z.string(),
  expectedEnd: z.string(),
  dueDate: z.string().nullable(),
  // Presentes só em ordem EM ANDAMENTO: quanto já saiu de peça boa e quanto falta.
  producedGoodQty: z.number().finite().nullable(),
  remainingQty: z.number().finite().nullable(),
  eligible: z.boolean(),
  reason: SimulatableOrderReasonSchema.nullable(),
});
export type SimulatableOrder = z.infer<typeof SimulatableOrderSchema>;

export const SimulatableOrdersResponseSchema = z.object({
  items: z.array(SimulatableOrderSchema),
});
export type SimulatableOrdersResponse = z.infer<typeof SimulatableOrdersResponseSchema>;

export const SimulatableOrdersEnvelopeSchema = z.object({
  data: SimulatableOrdersResponseSchema,
  error: z.null(),
});
